<script>
  import { onDestroy, onMount, tick, untrack } from 'svelte';
  import { session, startStudio, startVisualizer, startConfirmation, markInitialPromptSubmitted } from '../state/session.svelte.js';
  import { auth, openLoginModal } from '../state/auth.svelte.js';
  import {
    studioSettings,
    ttsSettings,
    setTTSEnabled,
    getEffectiveImagesPerBatch,
    getEffectiveModels
  } from '../state/settings.svelte.js';
  import { getWizardQuestion, getWizardQuestionsBatch, textToSpeech, generateWizardCombinations, describeImage } from '../utils/api.js';
  import { extractImageFile, imageFileToJpegDataUrl } from '../utils/image-to-prompt.js';
  import { addReferenceImage } from '../state/image-references.svelte.js';
  import { LLM_UI_TIMEOUT_MS } from '../llm/policy.js';
  import { onSpendReviewChange } from '../state/spend-review.svelte.js';
  import { createPausableTimeout } from '../utils/pausable-timeout.js';
  import { isSilentGenerationError } from '../utils/generation-error-details.js';
  import { primeAudioContext, playAudio, stopSpeaking } from '../utils/tts-audio.js';
  import { WIZARD_QUESTIONS_PER_STEP, MAX_WIZARD_SELECTIONS, emptyWizardAnswer,
    collectWizardFields, wizardAnswerReady } from '../utils/wizard-progress.js';
  import VoiceRecorder from './VoiceRecorder.svelte';
  import VoiceNotice from './VoiceNotice.svelte';
  import WizardQuestionCard from './WizardQuestionCard.svelte';
  import MatureContentSwitch from './MatureContentSwitch.svelte';

  let initialPrompt = $state('');
  let draftReady = $state(false);
  let localOwner = $state('');
  $effect(() => {
    if (!draftReady || localOwner !== wizardOwner()) return;
    session.wizardDraft = initialPrompt;
    session.wizardProgress = allQuestions.length ? {
      initialPrompt: questionsPrompt, questions: $state.snapshot(allQuestions),
      answers: $state.snapshot(questionAnswers), step: questionStep,
      focusedIndex: currentQuestionIndex, editingIdea
    } : null;
  });

  // Example prompts that cycle through on page load
  const examplePrompts = [
    "The first second of the universe, light tearing through darkness",
    "A lone astronaut standing beneath a shattered Dyson sphere",
    "An ancient dragon coiled around a floating crystal citadel",
    "A forest growing upside down into the night sky",
    "A cloaked figure walking through a burning cathedral",
    "A city at the edge of time",
    "Aurora borealis dancing over a frozen arctic lake",
    "Bioluminescent waves crashing on a midnight beach",
    "A total solar eclipse casting shadows across ancient ruins",
    "Lightning erupting from an active volcano at twilight",
    "The Milky Way arching over a silent desert canyon",
    "A single beam of light piercing through a cathedral of redwoods",
    "A colossal whale drifting through storm clouds, trailing lightning",
    "A neon samurai crossing a rain-slick megacity rooftop",
    "An ice palace melting into a sea of golden sand",
    "A lighthouse beaming through a galaxy-sized storm",
    "A train racing across a bridge made of starlight",
    "A fallen titan carved into a mountain, eyes still glowing",
    "A moonlit jungle where the trees hum with electricity",
    "A clockwork phoenix rising from a river of molten glass",
    "A desert caravan guided by a floating cathedral",
    "A cathedral built from bones and blooming orchids",
    "A storm of paper lanterns swirling above a silent ocean",
    "A midnight carnival on a drifting glacier",
    "A titan-sized statue waking beneath a city of mirrors",
    "An astronaut fishing in a lake of liquid aurora",
    "A ship sailing across a sky made of stained glass",
    "A solitary monk walking through a forest of crystal bells",
    "A meteor shower igniting a field of red flowers",
    "A black sun rising over an obsidian sea",
    "A citadel on the back of a wandering tortoise",
    "A waterfall cascading upward into a ringed planet",
    "A violinist on a rooftop summoning a thunderstorm",
    "A temple hidden inside a colossal storm cloud",
    "A city grown from coral under a blood-red moon",
    "A cosmic library where books orbit like planets",
    "A glacier splitting open to reveal a glowing throne",
    "A mirror desert reflecting a sky full of comets",
    "A colossal mechanical spider weaving a bridge of light",
    "An underwater opera house filled with luminous jellyfish",
    "A mountain monastery floating above a sea of fog",
    "A dragon made of constellations circling a frozen world",
    "A thunder god forging lightning into blades on a cliff",
    "A garden where flowers bloom into tiny galaxies",
    "A sunken city rising for one night of fire and music"
  ];
  let examplePromptOrder = $state([...examplePrompts]);
  let currentExampleIndex = $state(0);
  let showingExample = $state(false); // Whether to show the cycling example (starts false, becomes true after delay)
  let showInitialPlaceholder = $state(true); // Show "Begin creating" initially
  let exampleCycleInterval = null;
  let initialPlaceholderTimeout = null;

  // Reference for auto-resizing textarea
  let textareaRef = $state(null);

  // Voice recording state (from VoiceRecorder)
  let voiceState = $state('idle'); // 'idle' | 'recording' | 'processing'
  let voiceAudioLevels = $state(Array(48).fill(0));
  let voiceDuration = $state(0);
  let triggerVoiceStop = $state(false);
  let triggerVoiceCancel = $state(false);
  let answerRecordingStates = $state({});
  const answerRecorderBusy = $derived(Object.values(answerRecordingStates).some(state => state !== 'idle'));

  function handleVoiceStateChange({ state, audioLevels, duration }) {
    voiceState = state;
    voiceAudioLevels = audioLevels;
    voiceDuration = duration;

    // Reset triggers when state changes to idle
    if (state === 'idle') {
      triggerVoiceStop = false;
      triggerVoiceCancel = false;
    }
  }

  function formatVoiceDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // The wizard runs on the user's own Sogni account, so it requires sign-in.
  // Returns true (and opens the sign-in modal) when a signed-out user must sign
  // in first. While the boot-time SSO probe is still running, proceed: the LLM
  // call reports "still connecting" instead of asking a signed-in user to log in.
  function gateOnLogin() {
    if (auth.isLoggedIn || auth.isRestoring) return false;
    openLoginModal();
    return true;
  }

  // Combination generation state
  let isGeneratingCombinations = $state(false);

  // Calculate total prompt combinations from faceted fields
  const totalPromptCombinations = $derived(() => {
    if (fields.length === 0) return 1;
    return fields.reduce((total, field) => {
      const valueCount = field.values?.length || 1;
      return total * valueCount;
    }, 1);
  });
  let isLoading = $state(false);
  let isLoadingMoreOptions = $state(false);
  let error = $state(null);
  let optionsRefreshCount = $state(0);
  let isIPhone = $state(false);
  let longPressTimer = null;
  let longPressTriggered = false;
  const LONG_PRESS_MS = 650;
  // Watchdog only: it outlasts the LLM deadline, which ends the request first.
  const RESEND_TIMEOUT = LLM_UI_TIMEOUT_MS;
  let currentRequestToken = null;
  let currentRequestOwner = null;
  const requestTimeouts = new Set();
  let isSkippingWizard = $state(false);

  // Analyzing button rotation state
  const analyzingTexts = [
    'a subject',
    'a style',
    'an action',
    'an environment',
    'lighting',
    'a mood',
    'palettes',
    'compositions',
    'textures'
  ];
  let analyzingTextIndex = $state(0);
  let analyzingInterval = null;

  function startAnalyzingRotation() {
    stopAnalyzingRotation();
    analyzingTextIndex = 0;

    // Use variable timing to feel more like genuine AI thinking
    function scheduleNext() {
      // Random delay between 1.5 and 2.5 seconds for natural feel
      const delay = 1500 + Math.random() * 1000;
      analyzingInterval = setTimeout(() => {
        analyzingTextIndex = (analyzingTextIndex + 1) % analyzingTexts.length;
        scheduleNext();
      }, delay);
    }
    scheduleNext();
  }

  function stopAnalyzingRotation() {
    if (analyzingInterval) {
      clearTimeout(analyzingInterval);
      analyzingInterval = null;
    }
  }

  function resolvePromptFromExample() {
    if (showingExample && !initialPrompt) {
      initialPrompt = examplePromptOrder[currentExampleIndex];
      showingExample = false;
      stopExampleCycling();
    }
  }

  async function skipWizardToStudio() {
    if (!initialPrompt.trim()) return;
    if (isSkippingWizard || isLoading) return;
    // Rendering runs on the user's own account: signed out, sign in first.
    if (gateOnLogin()) return;

    console.log('🚀 Skipping wizard, going straight to studio');
    isSkippingWizard = true;

    const creativeBrief = {
      initialPrompt,
      fields: [],
      rawPrompt: initialPrompt.trim(), // Use the raw initial prompt
      needsFinalization: false // Don't need finalization, just use the prompt as-is
    };
    startStudio(creativeBrief);
  }

  function clearLongPressTimer() {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }

  function handleLongPressStart(event) {
    if (!isIPhone) return;
    if ((!initialPrompt.trim() && !showingExample) || isLoading || isSkippingWizard) return;

    if (event?.cancelable) {
      event.preventDefault();
    }
    longPressTriggered = false;
    clearLongPressTimer();
    longPressTimer = setTimeout(async () => {
      longPressTriggered = true;
      resolvePromptFromExample();
      await skipWizardToStudio();
    }, LONG_PRESS_MS);
  }

  function handleLongPressEnd() {
    clearLongPressTimer();
  }

  // Example prompt cycling functions
  function shufflePrompts(prompts) {
    const shuffled = [...prompts];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  function startExampleCycling() {
    stopExampleCycling();

    // Show "Begin creating" initially, then start cycling examples after 2 seconds
    showInitialPlaceholder = true;
    showingExample = false;

    initialPlaceholderTimeout = setTimeout(() => {
      // Shuffle once per page load for a fresh random sequence
      const promptPool = examplePrompts;
      examplePromptOrder = shufflePrompts(promptPool);
      currentExampleIndex = 0;
      showInitialPlaceholder = false;
      showingExample = true;

      exampleCycleInterval = setInterval(() => {
        currentExampleIndex = (currentExampleIndex + 1) % examplePromptOrder.length;
      }, 3000); // Cycle every 3 seconds
    }, 2000); // Wait 2 seconds before cycling examples
  }

  function stopExampleCycling() {
    if (initialPlaceholderTimeout) {
      clearTimeout(initialPlaceholderTimeout);
      initialPlaceholderTimeout = null;
    }
    if (exampleCycleInterval) {
      clearInterval(exampleCycleInterval);
      exampleCycleInterval = null;
    }
    showInitialPlaceholder = false;
  }

  // Get the display value for the textarea
  const textareaDisplayValue = $derived(
    initialPrompt ? initialPrompt : (showingExample ? examplePromptOrder[currentExampleIndex] : '')
  );

  // Get placeholder text - shows "Begin creating" initially, then nothing when examples cycle
  const placeholderText = $derived(
    showInitialPlaceholder ? 'Begin creating...' : ''
  );

  // Auto-resize textarea as user types
  function autoResizeTextarea() {
    if (!textareaRef) return;
    // Reset height to auto to get the correct scrollHeight
    textareaRef.style.height = 'auto';
    // Set height based on content, max 200px
    const maxHeight = 200;
    const newHeight = Math.min(textareaRef.scrollHeight, maxHeight);
    textareaRef.style.height = newHeight + 'px';
    // Add overflow-y if content exceeds max height
    textareaRef.style.overflowY = textareaRef.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  // Auto-resize when display value changes (e.g., when example cycles or user types)
  $effect(() => {
    // Access textareaDisplayValue to track it
    const _ = textareaDisplayValue;
    // Call resize after DOM updates
    if (textareaRef) {
      tick().then(() => autoResizeTextarea());
    }
  });

  // Batch questions state - all 4 questions fetched at once
  let allQuestions = $state([]);
  let questionsPrompt = $state('');
  let questionAnswers = $state([]);
  let questionStep = $state(0);
  let currentQuestionIndex = $state(0);
  let editingIdea = $state(false);
  const stepCount = $derived(Math.ceil(allQuestions.length / WIZARD_QUESTIONS_PER_STEP));
  const visibleQuestions = $derived(allQuestions.slice(questionStep * WIZARD_QUESTIONS_PER_STEP,
    (questionStep + 1) * WIZARD_QUESTIONS_PER_STEP));
  const fields = $derived(collectWizardFields(allQuestions, questionAnswers));
  const canAdvancePair = $derived(visibleQuestions.every((_, offset) =>
    wizardAnswerReady(questionAnswers[questionStep * WIZARD_QUESTIONS_PER_STEP + offset])));
  const wizardBusy = $derived(isLoadingMoreOptions || isGeneratingCombinations || answerRecorderBusy);

  // Derived current question from batch
  const currentQuestion = $derived(
    !editingIdea && allQuestions.length > 0 && currentQuestionIndex < allQuestions.length
      ? allQuestions[currentQuestionIndex]
      : null
  );
  const showAudioGuidance = $derived(
    !currentQuestion && !isLoading && !isSkippingWizard
  );

  const tips = $derived(() => {
    const items = [];
    items.push({
      id: 'image-drop',
      type: 'text',
      label: "Drag'n drop an image -> convert it to text prompt!"
    });
    items.push({
      id: 'skip-wizard',
      type: 'text',
      label: isIPhone
        ? 'Long-press submit: jump straight into studio'
        : 'Shift + submit: jump straight into studio'
    });
    if (!ttsSettings.enabled) {
      items.push({ id: 'enable-audio', type: 'action', label: 'Enable audio guidance' });
    }
    if (!auth.isLoggedIn) {
      items.push({ id: 'login', type: 'text', label: "Don't forget to sign in" });
    }
    items.push({ id: 'tagline', type: 'text', label: 'You think it, we dream it.' });
    return items;
  });
  let tipIndex = $state(0);
  let tipInterval = null;
  const TIP_ROTATION_MS = 5200;

  function getSafeTipIndex(items, index) {
    if (!items.length) return 0;
    const normalized = Number.isInteger(index) ? index : 0;
    return ((normalized % items.length) + items.length) % items.length;
  }

  $effect(() => {
    const currentTips = tips();
    if (!currentTips.length) {
      tipIndex = 0;
      return;
    }

    if (!Number.isInteger(tipIndex) || tipIndex < 0 || tipIndex >= currentTips.length) {
      tipIndex = 0;
    }
  });

  $effect(() => {
    const currentTips = tips();
    if (!showAudioGuidance || currentTips.length <= 1) {
      if (tipInterval) {
        clearInterval(tipInterval);
        tipInterval = null;
      }
      return;
    }

    if (tipInterval) {
      clearInterval(tipInterval);
    }
    tipInterval = setInterval(() => {
      const rotatingTips = tips();
      if (rotatingTips.length <= 1) {
        tipIndex = 0;
        return;
      }
      const safeIndex = getSafeTipIndex(rotatingTips, tipIndex);
      tipIndex = (safeIndex + 1) % rotatingTips.length;
    }, TIP_ROTATION_MS);

    return () => {
      if (tipInterval) {
        clearInterval(tipInterval);
        tipInterval = null;
      }
    };
  });

  // Audio pre-caching state
  // Map: questionIndex -> { url: string, status: 'loading' | 'ready' | 'error' }
  let audioCache = $state(new Map());
  const audioRequests = new Map();
  let narrationGeneration = 0;
  let disposed = false;

  function wizardOwner() {
    return `${(auth.walletAddress || '').toLowerCase()}:${session.id}`;
  }

  function isCurrentWizardOwner(owner) {
    return !disposed && owner === localOwner && owner === wizardOwner();
  }

  function isCurrentWizardRequest(token) {
    return currentRequestToken === token && isCurrentWizardOwner(currentRequestOwner);
  }

  function cancelNarration() {
    narrationGeneration += 1;
    stopSpeaking();
    for (const request of audioRequests.values()) request.controller.abort();
    audioRequests.clear();
    // Ready URLs can be replayed; aborted in-flight entries must be retryable.
    audioCache = new Map([...audioCache].filter(([, cached]) => cached.status === 'ready'));
  }

  function installWizardDraft(owner) {
    currentRequestToken = null;
    currentRequestOwner = null;
    clearLongPressTimer();
    longPressTriggered = false;
    clearAllRequestTimeouts();
    cancelNarration();
    cleanupAudioCache();
    isLoading = false;
    isSkippingWizard = false;
    isLoadingMoreOptions = false;
    isGeneratingCombinations = false;
    error = null;
    answerRecordingStates = {};
    isDescribingImage = false;
    describeImageError = '';
    describeThumbnailUrl = '';
    initialPrompt = session.wizardDraft ?? session.creativeBrief?.initialPrompt ?? session.creativeBrief?.rawPrompt ?? '';
    const progress = session.wizardProgress;
    allQuestions = progress?.questions || [];
    questionsPrompt = progress?.initialPrompt || '';
    questionAnswers = progress?.answers || [];
    questionStep = progress?.step || 0;
    currentQuestionIndex = progress?.focusedIndex || 0;
    editingIdea = Boolean(progress && (progress.editingIdea || questionsPrompt !== initialPrompt));
    localOwner = owner;
    stopAnalyzingRotation();
    stopExampleCycling();
    // Hydration uses saved questions and drafts without a model or voice call.
  }

  $effect(() => {
    const owner = wizardOwner();
    if (draftReady && owner !== localOwner) untrack(() => installWizardDraft(owner));
  });

  $effect(() => {
    if (!ttsSettings.enabled) untrack(cancelNarration);
  });

  const promptStyleText = $derived(() => (studioSettings.promptStylesAlike || '').trim());

  function withPromptStylePreference(options = {}) {
    const stylePreference = promptStyleText();
    if (!stylePreference) {
      return options;
    }
    return {
      ...options,
      promptStylePreference: stylePreference
    };
  }

  // Handle keyboard shortcuts for option selection (1-4 keys)
  function handleGlobalKeydown(event) {
    // Only handle when in Q&A phase and not loading
    if (!currentQuestion || isLoading || wizardBusy) return;

    // Don't handle if user is typing in an input field
    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      return;
    }

    // Check for number keys 1-6 - now toggles selection
    const keyNum = parseInt(event.key);
    if (keyNum >= 1 && keyNum <= 6) {
      const presets = currentQuestion.presets;
      if (presets && presets.length >= keyNum) {
        event.preventDefault();
        const preset = presets[keyNum - 1];
        const presetValue = typeof preset === 'object' ? preset.description : preset;
        togglePreset(currentQuestionIndex, presetValue);
      }
    }
  }

  // Toggle a preset selection (multi-select with limit)
  function focusQuestion(index) {
    if (currentQuestionIndex === index) return;
    cancelNarration();
    currentQuestionIndex = index;
  }

  function togglePreset(index, presetValue) {
    focusQuestion(index);
    const newSet = new Set(questionAnswers[index].selectedOptions);
    if (newSet.has(presetValue)) {
      newSet.delete(presetValue);
    } else {
      // Enforce max selection limit to prevent combinatorial explosion
      if (newSet.size >= MAX_WIZARD_SELECTIONS) {
        // Already at max, don't add more
        return;
      }
      newSet.add(presetValue);
    }
    questionAnswers[index] = { selectedOptions: [...newSet], customInput: '', skipped: false };
  }

  function setCustomAnswer(index, value) {
    focusQuestion(index);
    questionAnswers[index] = { selectedOptions: [], customInput: value, skipped: false };
  }

  function skipQuestion(index) {
    focusQuestion(index);
    questionAnswers[index] = { ...emptyWizardAnswer(), skipped: true };
  }

  let cleanupKeyboardListener = null;

  onMount(() => {
    if (typeof window === 'undefined') return;
    installWizardDraft(wizardOwner());
    draftReady = true;

    isIPhone = /iPhone|iPad|iPod/.test(navigator.userAgent || '') || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Add keyboard listener for 1-4 shortcuts
    window.addEventListener('keydown', handleGlobalKeydown);
    cleanupKeyboardListener = () => {
      window.removeEventListener('keydown', handleGlobalKeydown);
      cleanupKeyboardListener = null;
    };

    // A restored brief remains editable when the input receives focus.
    if (initialPrompt.trim()) stopExampleCycling();
    else startExampleCycling();

    return () => {
      if (cleanupKeyboardListener) {
        cleanupKeyboardListener();
      }
      stopExampleCycling();
    };
  });

  function createRequestTimeout(handler, delay) {
    const id = createPausableTimeout(() => {
      requestTimeouts.delete(id);
      handler();
    }, delay, onSpendReviewChange);
    requestTimeouts.add(id);
    return id;
  }

  function clearRequestTimeoutSafe(id) {
    if (requestTimeouts.has(id)) {
      id();
      requestTimeouts.delete(id);
    }
  }

  function clearAllRequestTimeouts() {
    requestTimeouts.forEach(cancel => cancel());
    requestTimeouts.clear();
  }

  // Only the focused question is narrated. Text and answers never wait for it.
  async function prefetchAudio(questionIndex) {
    if (!ttsSettings.enabled) return null;
    const question = allQuestions[questionIndex];
    if (!question?.question) return null;
    const cached = audioCache.get(questionIndex);
    if (cached?.status === 'ready') return cached.url;
    if (audioRequests.has(questionIndex)) return audioRequests.get(questionIndex).promise;

    const owner = localOwner;
    const controller = new AbortController();
    audioCache.set(questionIndex, { status: 'loading', url: null });
    audioCache = new Map(audioCache);
    const request = { controller, promise: null };
    request.promise = (async () => {
      try {
        const audioBlob = await textToSpeech(question.question, undefined, { signal: controller.signal });
        if (controller.signal.aborted || !isCurrentWizardOwner(owner) || allQuestions[questionIndex] !== question) return null;
        const url = URL.createObjectURL(audioBlob);
        audioCache.set(questionIndex, { status: 'ready', url });
        audioCache = new Map(audioCache);
        return url;
      } catch (err) {
        if (!controller.signal.aborted && isCurrentWizardOwner(owner) && allQuestions[questionIndex] === question) {
          console.error('Question narration failed:', err);
          audioCache.set(questionIndex, { status: 'error', url: null });
          audioCache = new Map(audioCache);
        }
        return null;
      } finally {
        if (audioRequests.get(questionIndex) === request) audioRequests.delete(questionIndex);
      }
    })();
    audioRequests.set(questionIndex, request);
    return request.promise;
  }

  async function playQuestionAudio(index) {
    if (!ttsSettings.enabled || answerRecorderBusy) return;
    primeAudioContext();
    focusQuestion(index);
    cancelNarration();
    const generation = narrationGeneration;
    const owner = localOwner;
    const question = allQuestions[index];
    const url = await prefetchAudio(index);
    if (url && generation === narrationGeneration && isCurrentWizardOwner(owner) &&
      currentQuestionIndex === index && allQuestions[index] === question && !editingIdea && ttsSettings.enabled) {
      await playAudio(url);
    }
  }

  async function retryCurrentAudio() {
    const cached = audioCache.get(currentQuestionIndex);
    if (cached?.url) URL.revokeObjectURL(cached.url);
    audioCache.delete(currentQuestionIndex);
    audioCache = new Map(audioCache);
    await playQuestionAudio(currentQuestionIndex);
  }

  // Handle initial prompt submission - fetches ALL 4 questions at once
  async function handleInitialSubmit(event) {
    if (!initialPrompt.trim() || isLoading || isSkippingWizard) return;
    const shiftKey = !!event?.shiftKey;

    // Signed-out users sign in first. Don't mark the prompt submitted yet, or
    // cancelling would permanently hide the showcase.
    if (gateOnLogin()) {
      return;
    }

    // User is proceeding for real now - safe to retire the homepage showcase.
    markInitialPromptSubmitted();

    // Prime audio context immediately on user interaction (Safari workaround)
    // Must happen synchronously within the click handler before any async work
    primeAudioContext();

    // Dev shortcut: Shift+click to skip wizard and go straight to studio
    if (shiftKey) {
      await skipWizardToStudio();
      return;
    }

    // Editing and returning to the same idea reuses the saved batch and drafts.
    if (allQuestions.length && initialPrompt === questionsPrompt) {
      const owner = localOwner;
      editingIdea = false;
      await tick();
      if (isCurrentWizardOwner(owner)) playQuestionAudio(currentQuestionIndex);
      return;
    }

    isLoading = true;
    error = null;
    startAnalyzingRotation();

    clearAllRequestTimeouts();

    const requestToken = Symbol();
    const requestOwner = localOwner;
    currentRequestToken = requestToken;
    currentRequestOwner = requestOwner;

    try {
      console.log('🧙 Fetching all 4 questions at once...');
      const response = await requestWizardQuestionsBatchWithRetry(requestToken);

      if (!isCurrentWizardRequest(requestToken)) return;

      if (response.type === 'wizard:questions-batch' && response.questions) {
        console.log(`✅ Received ${response.questions.length} questions`);

        // Make both cards usable immediately; narration runs independently.
        cancelNarration();
        cleanupAudioCache();
        allQuestions = response.questions;
        questionsPrompt = initialPrompt;
        questionAnswers = allQuestions.map(emptyWizardAnswer);
        questionStep = 0;
        currentQuestionIndex = 0;
        editingIdea = false;
        isLoading = false;
        currentRequestToken = null;
        currentRequestOwner = null;
        clearAllRequestTimeouts();
        stopAnalyzingRotation();
        await tick();
        if (isCurrentWizardOwner(requestOwner)) playQuestionAudio(0);
      }
    } catch (err) {
      if (!isCurrentWizardRequest(requestToken)) return;

      console.error('Wizard batch error:', err);

      const message = String(err?.message || '');
      if (isSilentGenerationError(err)) {
        // The sign-in modal is already open.
        error = null;
      } else if (err?.kind) {
        // Classified LLM failure (connecting, balance, offline, timeout…) with
        // user-facing copy from src/lib/llm/policy.js.
        error = message;
      } else if (/timeout|timed out/i.test(message)) {
        error = 'Request timed out. Please try again.';
      } else if (/connect/i.test(message)) {
        error = 'LLM server unavailable. Please try again.';
      } else if (err.message?.includes('Invalid input')) {
        error = err.message;
      } else {
        error = 'Unable to generate questions. Please try again.';
      }

      isLoading = false;
      currentRequestToken = null;
      currentRequestOwner = null;
      clearAllRequestTimeouts();
      stopAnalyzingRotation();
    }
  }

  // One batch with a pausable watchdog; never resend an in-flight account call.
  function requestWizardQuestionsBatchWithRetry(token) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const localTimeouts = new Set();

      const clearLocalTimeouts = () => {
        for (const id of localTimeouts) {
          clearRequestTimeoutSafe(id);
        }
        localTimeouts.clear();
      };

      const startAttempt = () => {
        const attemptPromise = getWizardQuestionsBatch(
          initialPrompt,
          withPromptStylePreference()
        );

        attemptPromise.then(response => {
          if (settled || !isCurrentWizardRequest(token)) return;
          settled = true;
          clearLocalTimeouts();
          resolve(response);
        }).catch(err => {
          if (settled || !isCurrentWizardRequest(token)) return;
          settled = true;
          clearLocalTimeouts();
          reject(err);
        });

        const timeoutId = createRequestTimeout(() => {
          localTimeouts.delete(timeoutId);
          if (settled || !isCurrentWizardRequest(token)) return;

          // No resend: a second request would run (and bill) alongside the
          // first, which is still in flight on the user's account.
          settled = true;
          clearLocalTimeouts();
          reject(new Error('timeout'));
        }, RESEND_TIMEOUT);

        localTimeouts.add(timeoutId);
      };

      startAttempt();
    });
  }

  async function moveToStep(step) {
    const owner = localOwner;
    cancelNarration();
    questionStep = step;
    currentQuestionIndex = step * WIZARD_QUESTIONS_PER_STEP;
    error = null;
    await tick();
    if (!isCurrentWizardOwner(owner)) return;
    scrollToTop();
    document.getElementById(`wizard-question-${currentQuestionIndex}`)?.focus();
    playQuestionAudio(currentQuestionIndex);
  }

  async function handleNextPair() {
    if (!canAdvancePair || wizardBusy) return;
    if (questionStep + 1 < stepCount) await moveToStep(questionStep + 1);
  }

  function editIdea() {
    cancelNarration();
    editingIdea = true;
    error = null;
    tick().then(() => textareaRef?.focus());
  }

  async function handleAnswerEnter(index) {
    if (wizardBusy) return;
    const nextIndex = index + 1;
    if (nextIndex < (questionStep + 1) * WIZARD_QUESTIONS_PER_STEP) {
      document.getElementById(`wizard-answer-${nextIndex}`)?.focus();
    } else if (canAdvancePair && questionStep + 1 < stepCount) await handleNextPair();
  }

  // Generate final prompt and start studio
  async function handleLetsGo() {
    if (wizardBusy) return;
    if (gateOnLogin()) {
      return;
    }

    // Prime the shared audio context for iOS Safari - MUST be synchronous at start of click handler
    primeAudioContext();

    cancelNarration();

    if (fields.some(field => field.isFaceted)) await generateAndShowCombinations();
    else proceedToStudio();
  }

  // Use the existing combination path; arriving at its preview does not wait
  // for another spoken summary or submit an image project.
  async function generateAndShowCombinations() {
    const owner = localOwner;
    const prompt = initialPrompt;
    const collectedFields = $state.snapshot(fields);
    isGeneratingCombinations = true;
    error = null;
    try {
      const response = await generateWizardCombinations(prompt, collectedFields);
      if (!isCurrentWizardOwner(owner)) return false;
      if (response.success) {
        startConfirmation({ initialPrompt: prompt, fields: collectedFields,
          rawPrompt: null, needsFinalization: false }, response.combinations, response.stats);
        return true;
      }
      error = 'Unable to prepare your prompt combinations. Please try again.';
      return false;
    } catch (err) {
      if (!isCurrentWizardOwner(owner)) return false;
      console.error('Error generating combinations:', err);
      if (!isSilentGenerationError(err)) {
        error = err?.message || 'Unable to prepare your prompt combinations. Please try again.';
      }
      return false;
    } finally {
      if (isCurrentWizardOwner(owner)) isGeneratingCombinations = false;
    }
  }

  // Proceed to studio without faceted prompts
  function proceedToStudio() {
    const creativeBrief = {
      initialPrompt,
      fields,
      rawPrompt: null,
      needsFinalization: true
    };

    startStudio(creativeBrief);
  }

  // Open the prompt visualizer for faceting
  async function handleVisualizePrompt() {
    if (wizardBusy || gateOnLogin()) return;
    cancelNarration();

    const creativeBrief = {
      initialPrompt,
      fields,
      rawPrompt: null,
      needsFinalization: true
    };

    startVisualizer(creativeBrief);
  }

  // Refresh only the chosen card; retain the other three drafts.
  async function getMoreOptions(index) {
    if (wizardBusy) return;
    focusQuestion(index);
    cancelNarration();
    const question = allQuestions[index];
    const owner = localOwner;
    isLoadingMoreOptions = true;
    error = null;
    try {
      optionsRefreshCount++;
      const response = await getWizardQuestion(initialPrompt, fields, false,
        withPromptStylePreference({ refreshOptions: true, seed: optionsRefreshCount,
          previousPresets: question.presets, currentQuestionContext: {
            question: question.question, fieldName: question.fieldName, pillar: question.pillar
          } }));
      if (!isCurrentWizardOwner(owner) || allQuestions[index] !== question) return;
      if (response.type === 'wizard:question') {
        const cached = audioCache.get(index);
        if (cached?.url) URL.revokeObjectURL(cached.url);
        audioCache.delete(index);
        audioCache = new Map(audioCache);
        allQuestions[index] = { ...question, question: response.result.question, presets: response.result.presets };
        questionAnswers[index] = emptyWizardAnswer();
      }
    } catch (err) {
      if (!isCurrentWizardOwner(owner)) return;
      error = isSilentGenerationError(err) ? null
        : err?.kind ? err.message : 'Unable to generate more options. Please try again.';
      console.error('More options error:', err);
    } finally {
      if (isCurrentWizardOwner(owner)) isLoadingMoreOptions = false;
    }
  }

  function setAnswerRecordingState(index, state) {
    untrack(() => {
      if (answerRecordingStates[index] === state) return;
      answerRecordingStates = { ...answerRecordingStates, [index]: state };
      if (state !== 'idle') {
        focusQuestion(index);
        cancelNarration();
      }
    });
  }

  // Handle Enter key in textarea
  function handleKeydown(event) {
    if (event.key === 'Enter') {
      // Shift+Enter adds a newline (don't submit)
      if (event.shiftKey) {
        // Allow default behavior (newline)
        // After the newline is added, resize the textarea
        setTimeout(() => autoResizeTextarea(), 0);
        return;
      }

      event.preventDefault();
      if (!currentQuestion) {
        // If showing example, use the example as the prompt
        if (showingExample && !initialPrompt) {
          initialPrompt = examplePromptOrder[currentExampleIndex];
          showingExample = false;
          stopExampleCycling();
        }
        handleInitialSubmit(event);
      }
    }
  }

  // Handle voice transcript for initial prompt
  function handleVoiceTranscript(transcript) {
    if (transcript) {
      initialPrompt = transcript;
      // Stop showing examples when user uses voice input
      showingExample = false;
      stopExampleCycling();
      // TTS is now controlled globally via header icon
    }
  }

  // Image drop → describe state
  let isDescribingImage = $state(false);
  let describeImageError = $state('');
  let describeThumbnailUrl = $state('');
  let dragDepth = $state(0);

  function isFileDrag(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }

  function handleDragEnter(event) {
    if (!isFileDrag(event) || isLoading || isSkippingWizard || isDescribingImage || voiceState !== 'idle') return;
    event.preventDefault();
    dragDepth += 1;
  }

  function handleDragOver(event) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  function handleDragLeave(event) {
    if (!isFileDrag(event)) return;
    dragDepth = Math.max(0, dragDepth - 1);
  }

  function handleImageDrop(event) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    dragDepth = 0;
    if (isLoading || isSkippingWizard || isDescribingImage || voiceState !== 'idle') return;

    const file = extractImageFile(event.dataTransfer);
    if (!file) {
      describeImageError = 'Drop an image file';
      return;
    }
    handleImageInput(file);
  }

  function handleImagePaste(event) {
    if (voiceState !== 'idle') return;
    const file = extractImageFile(event.clipboardData);
    if (!file) return; // normal text paste — let the default happen
    event.preventDefault();
    handleImageInput(file);
  }

  async function handleImageInput(file) {
    if (isLoading || isSkippingWizard || isDescribingImage || voiceState !== 'idle') return;

    const owner = localOwner;
    describeImageError = '';
    isDescribingImage = true;

    // Mirror handleVoiceTranscript: stop the example cycling once real input arrives
    showingExample = false;
    showInitialPlaceholder = false;
    stopExampleCycling();

    try {
      // Description uses a small vision thumbnail; retain the original for the
      // render setup's explicit "Use for generation" choice.
      await addReferenceImage(file, { enabled: false });
      if (!isCurrentWizardOwner(owner)) return;
      const dataUrl = await imageFileToJpegDataUrl(file);
      if (!isCurrentWizardOwner(owner)) return;
      describeThumbnailUrl = dataUrl;
      const description = await describeImage(dataUrl);
      if (!isCurrentWizardOwner(owner)) return;
      initialPrompt = description;
      await tick();
      // Textarea is remounting; the textareaDisplayValue $effect re-runs the resize after remount
      autoResizeTextarea();
    } catch (error) {
      if (isCurrentWizardOwner(owner) && !isSilentGenerationError(error)) describeImageError = error.message || 'Unable to read your image. Please try again.';
    } finally {
      if (isCurrentWizardOwner(owner)) {
        isDescribingImage = false;
        describeThumbnailUrl = '';
      }
    }
  }

  // Clean up audio cache URLs to prevent memory leaks
  function cleanupAudioCache() {
    for (const [_, cached] of audioCache) {
      if (cached && cached.url) {
        URL.revokeObjectURL(cached.url);
      }
    }
    audioCache.clear();
  }

  onDestroy(() => {
    clearLongPressTimer();
    clearAllRequestTimeouts();
    stopAnalyzingRotation();
    stopExampleCycling();
    if (tipInterval) {
      clearInterval(tipInterval);
      tipInterval = null;
    }
    disposed = true;
    cancelNarration();
    // Clean up audio cache URLs to prevent memory leaks
    cleanupAudioCache();
  });

  // Scroll to top helper - used when questions appear or change
  function scrollToTop() {
    if (typeof window === 'undefined') return;

    // Multiple scroll attempts to ensure it works on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }


</script>

<div class="wizard-container" class:with-questions={!!currentQuestion}>
  <VoiceNotice retry={currentQuestion ? retryCurrentAudio : null} />
  {#key localOwner}
  <div class="wizard-prompt">
    {#if !currentQuestion}
      <!-- Initial prompt input -->
      <h1>What will you dream?</h1>
      <p class="wizard-intro">4 questions · 2 steps. Or jump straight into Studio.</p>

      <div
        class="chat-input-container"
        role="group"
        aria-label="Prompt input and image drop area"
        class:disabled={isLoading || isSkippingWizard}
        class:recording={voiceState === 'recording'}
        class:processing={voiceState === 'processing'}
        class:describing={isDescribingImage}
        class:drag-active={dragDepth > 0}
        ondragenter={handleDragEnter}
        ondragover={handleDragOver}
        ondragleave={handleDragLeave}
        ondrop={handleImageDrop}
      >
        {#if dragDepth > 0}
          <div class="drop-hint">Drop image to describe it</div>
        {/if}
        <!-- Recording mode: X cancel on left, waveform in middle, checkbox on right -->
        {#if voiceState === 'recording'}
          <div class="recording-waveform">
            <div class="waveform-bars">
              {#each voiceAudioLevels as level}
                <div class="waveform-bar" style="height: {4 + level * 20}px;"></div>
              {/each}
            </div>
            <span class="recording-timer">{formatVoiceDuration(voiceDuration)}</span>
          </div>

          <button
            class="btn-cancel-recording"
            onclick={() => triggerVoiceCancel = true}
            title="Cancel recording (Esc)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <button
            class="btn-submit-recording"
            onclick={() => triggerVoiceStop = true}
            title="Submit recording"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
        {:else if voiceState === 'processing'}
          <!-- Processing mode: show spinner -->
          <div class="processing-indicator">
            <span class="spinner"></span>
            <span>Transcribing...</span>
          </div>
        {:else if isDescribingImage}
          <!-- Image describe mode: thumbnail + spinner -->
          <div class="processing-indicator">
            {#if describeThumbnailUrl}
              <img class="describe-thumb" src={describeThumbnailUrl} alt="Selected upload preview" />
            {/if}
            <span class="spinner"></span>
            <span>Reading your image…</span>
          </div>
        {:else}
          <!-- Normal mode: textarea with buttons -->
          <textarea
            aria-label="Describe your image idea"
            bind:this={textareaRef}
            value={textareaDisplayValue}
            onfocus={() => {
              if (showingExample || showInitialPlaceholder) {
                // User clicked into box, clear example and stop cycling
                showingExample = false;
                showInitialPlaceholder = false;
                stopExampleCycling();
                initialPrompt = '';
              }
            }}
            oninput={(e) => {
              initialPrompt = e.target.value;
              describeImageError = '';
              autoResizeTextarea();
            }}
            onpaste={handleImagePaste}
            placeholder={placeholderText}
            maxlength="2000"
            disabled={isLoading || isSkippingWizard}
            onkeydown={handleKeydown}
            class:showing-example={showingExample && !initialPrompt}
            rows="1"
          ></textarea>

          <div class="chat-input-actions">
            <div class="input-buttons">
              <button
                class="btn-submit-arrow"
                onclick={(e) => {
                  if (longPressTriggered) {
                    longPressTriggered = false;
                    return;
                  }
                  // If showing example, use the example as the prompt
                  resolvePromptFromExample();
                  handleInitialSubmit(e);
                }}
                oncontextmenu={(e) => {
                  if (isIPhone) {
                    e.preventDefault();
                  }
                }}
                ontouchstart={handleLongPressStart}
                ontouchend={handleLongPressEnd}
                ontouchcancel={handleLongPressEnd}
                disabled={(!initialPrompt.trim() && !showingExample) || isLoading || isSkippingWizard}
                title="Begin Creating"
                class:ios-long-press={isIPhone}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5"></line>
                  <polyline points="5 12 12 5 19 12"></polyline>
                </svg>
              </button>
            </div>
          </div>
        {/if}

        <!-- VoiceRecorder always mounted - mic button only visible when idle -->
        <div class="voice-recorder-mount" class:show-mic={voiceState === 'idle' && !isLoading && !isSkippingWizard && !isDescribingImage}>
          <VoiceRecorder
            onTranscript={handleVoiceTranscript}
            onStateChange={handleVoiceStateChange}
            disabled={isLoading || isSkippingWizard}
            compact={true}
            externalStop={triggerVoiceStop}
            externalCancel={triggerVoiceCancel}
          />
        </div>
      </div>

      {#if describeImageError}
        <div class="describe-error" role="alert">{describeImageError}</div>
      {/if}

      <div class="direct-start">
        <button type="button" class="audio-guidance-link" onclick={() => {
          resolvePromptFromExample();
          skipWizardToStudio();
        }} disabled={(!initialPrompt.trim() && !showingExample) || isLoading || isSkippingWizard ||
          isDescribingImage || voiceState !== 'idle'}>Use my idea directly</button>
        <!-- Opt in to mature themes before the first prompt call (signed in only). Hidden
             while a prompt call runs: the approval fingerprints settings, so a toggle mid-call
             would fail the next rung. -->
        {#if !isLoading && !isSkippingWizard}
          <MatureContentSwitch compact />
        {/if}
      </div>

      <div class="audio-guidance-slot" aria-hidden={!showAudioGuidance}>
        {#if showAudioGuidance}
          {@const tipItems = tips()}
        {#if tipItems.length}
          {@const activeTip = tipItems[getSafeTipIndex(tipItems, tipIndex)]}
          {#if activeTip}
          {#key activeTip.id}
            <div class="audio-guidance-rotator">
              {#if activeTip.type === 'action'}
                <button
                  type="button"
                  class="audio-guidance-link"
                  onclick={() => {
                    primeAudioContext();
                    setTTSEnabled(true);
                  }}
                  disabled={isLoading || isSkippingWizard}
                >
                  {activeTip.label}
                </button>
              {:else}
                <span class="audio-guidance-tip">{activeTip.label}</span>
              {/if}
            </div>
          {/key}
          {/if}
        {/if}
        {/if}
      </div>

      {#if isLoading || isSkippingWizard}
        <div class="loading-status below-input">
          <span class="spinner"></span>
          <span class="loading-text">
            {#if isSkippingWizard}
              Skipping wizard...
            {:else}
              {#if analyzingTexts[analyzingTextIndex] === 'palettes'}
                Generating Palettes...
              {:else if analyzingTexts[analyzingTextIndex] === 'lighting'}
                Creating lighting scenarios...
              {:else if analyzingTexts[analyzingTextIndex] === 'a mood'}
                Parsing mood...
              {:else}
                Analyzing {analyzingTexts[analyzingTextIndex]}...
              {/if}
            {/if}
          </span>
        </div>
        {#if isLoading && !isSkippingWizard}
          <p class="loading-eta">Preparing 4 questions for 2 steps…</p>
        {/if}
      {/if}

      {#if error}
        <div class="error-message mt-2">
          {error}
        </div>
      {/if}
    {:else}
      <div class="wizard-qa">
        <div class="question-progress">Step {questionStep + 1} of {stepCount} · 4 questions, 2 at a time</div>
        <p class="pair-intro">Choose answers for both questions, or skip anything your idea already covers.</p>
        <div class="question-pair">
          {#each visibleQuestions as question, offset (`${localOwner}:${question.fieldName}:${question.question}`)}
            {@const index = questionStep * WIZARD_QUESTIONS_PER_STEP + offset}
            <WizardQuestionCard {question} {index} answer={questionAnswers[index]}
              active={currentQuestionIndex === index}
              disabled={isGeneratingCombinations || isLoadingMoreOptions || answerRecorderBusy}
              refreshing={isLoadingMoreOptions && currentQuestionIndex === index}
              narrationEnabled={ttsSettings.enabled}
              narrationLoading={audioCache.get(index)?.status === 'loading'}
              anotherRecorderBusy={answerRecorderBusy && answerRecordingStates[index] !== 'recording' &&
                answerRecordingStates[index] !== 'starting' && answerRecordingStates[index] !== 'processing'}
              onFocus={() => focusQuestion(index)}
              onToggle={value => togglePreset(index, value)}
              onCustomInput={value => setCustomAnswer(index, value)}
              onSkip={() => skipQuestion(index)}
              onRefresh={() => getMoreOptions(index)}
              onListen={() => playQuestionAudio(index)}
              onEnter={() => handleAnswerEnter(index)}
              onRecordingChange={state => setAnswerRecordingState(index, state)} />
          {/each}
        </div>

        {#if fields.some(field => field.isFaceted)}
          <p class="variant-summary">
            {totalPromptCombinations()} prompt variations ·
            {totalPromptCombinations() * getEffectiveModels().length * getEffectiveImagesPerBatch()} images with current settings.
            Review models and charges before rendering.
          </p>
        {/if}

        <div class="wizard-navigation">
        <div class="wizard-actions">
          <button type="button" class="btn-outline"
            onclick={() => questionStep > 0 ? moveToStep(questionStep - 1) : editIdea()} disabled={wizardBusy}>
            {questionStep > 0 ? 'Back' : 'Edit idea'}
          </button>
          {#if questionStep + 1 < stepCount}
            <button type="button" class="btn-primary" onclick={handleNextPair}
              disabled={!canAdvancePair || wizardBusy}>Next two questions</button>
          {:else}
            <button type="button" class="btn-outline" onclick={handleVisualizePrompt}
              disabled={!canAdvancePair || wizardBusy}>Visualize Prompt</button>
            <button type="button" class="btn-primary" onclick={handleLetsGo}
              disabled={!canAdvancePair || wizardBusy}>Let's Go!</button>
          {/if}
        </div>
        {#if questionStep + 1 < stepCount && fields.length > 0}
          <div class="early-finish">
            <button type="button" class="audio-guidance-link" onclick={handleLetsGo} disabled={wizardBusy}>
              Use these answers
            </button>
            <span>Finish now and keep the rest of your original idea.</span>
          </div>
        {/if}
        </div>
        <p class="multi-select-hint text-small text-muted">
          Press <kbd>1</kbd>–<kbd>6</kbd> to select options in the focused question.
        </p>
        {#if error}<div class="error-message mt-2" role="alert">{error}</div>{/if}
      </div>
    {/if}
  </div>
  {/key}
</div>

<!-- Loading overlay for generating combinations -->
{#if isGeneratingCombinations}
  <div class="generating-overlay">
    <div class="generating-content">
      <span class="spinner"></span>
      <p class="generating-title">Refining Prompts</p>
      <p class="generating-subtitle">Your selections have created {totalPromptCombinations()} unique prompt{totalPromptCombinations() > 1 ? 's' : ''}. We're enhancing each one for more detail and accuracy.</p>
    </div>
  </div>
{/if}


<style>
  /* Leave room at the bottom so content (e.g. "Or enter your own:") scrolls
     clear of the app's fixed signed-out banner instead of hiding under it. */
  .wizard-container {
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  }

  /* ChatGPT-style input container - single line */
  .chat-input-container {
    background: var(--color-surface);
    border: 2px solid var(--color-border);
    border-radius: 24px;
    padding: 8px 12px;
    padding-left: 20px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    max-width: 600px;
    margin: 0 auto;
    min-height: 48px;
    position: relative;
  }

  .chat-input-container:focus-within {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(255, 27, 141, 0.1);
  }

  .chat-input-container.disabled {
    opacity: 0.7;
    pointer-events: none;
  }

  .chat-input-container.processing {
    pointer-events: auto;
  }

  .chat-input-container.recording {
    border-color: var(--color-primary);
    background: var(--color-primary-light);
  }

  .chat-input-container.drag-active {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(255, 27, 141, 0.1);
  }

  .drop-hint {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, var(--color-surface) 88%, transparent);
    border-radius: inherit;
    color: var(--color-primary);
    font-weight: 600;
    font-size: 0.95rem;
    pointer-events: none;
    z-index: 2;
  }

  .describe-thumb {
    width: 28px;
    height: 28px;
    object-fit: cover;
    border-radius: 6px;
  }

  .describe-error {
    margin-top: 8px;
    color: var(--color-primary);
    font-size: 0.85rem;
    text-align: center;
  }

  .chat-input-container textarea {
    border: none !important;
    background: transparent !important;
    resize: none !important;
    outline: none !important;
    font-size: 1rem;
    line-height: 1.5;
    min-height: 24px !important;
    max-height: 200px;
    overflow-y: hidden;
    padding: 0 !important;
    /* Reserve space for overlaid mic + submit actions so text never runs under them */
    padding-right: 40px !important;
    width: auto;
    flex: 1;
    min-width: 0;
    font-family: inherit;
    box-shadow: none !important;
    margin-bottom: 0 !important;
  }

  .chat-input-container textarea:focus {
    border: none !important;
    box-shadow: none !important;
  }

  .chat-input-container textarea::placeholder {
    color: var(--color-text-muted);
    font-style: normal;
  }

  .chat-input-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-shrink: 0;
    position: relative;
    z-index: 2;
  }

  .ios-long-press {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    touch-action: manipulation;
  }

  .audio-guidance-link {
    display: block;
    width: fit-content;
    font-size: 0.95rem;
    font-weight: 400;
    color: var(--color-primary);
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: none;
    letter-spacing: 0.01em;
    transition: color 0.2s ease, opacity 0.2s ease;
  }

  .audio-guidance-slot {
    margin: 28px auto 0;
    display: flex;
    justify-content: center;
    min-height: 1.4rem;
  }

  .audio-guidance-rotator {
    animation: tipFadeIn 0.5s ease;
  }

  .audio-guidance-link:hover:not(:disabled),
  .audio-guidance-link:focus-visible:not(:disabled) {
    color: var(--color-primary-hover);
  }

  .audio-guidance-link:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .audio-guidance-tip {
    display: block;
    font-size: 0.95rem;
    color: #2a2a2a;
    letter-spacing: 0.01em;
  }

  @keyframes tipFadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .input-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .btn-submit-arrow {
    width: 36px;
    min-height: 36px;
    max-height: 36px;
    padding: 0;
    border-radius: 50%;
    border: none;
    background: var(--color-primary);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .btn-submit-arrow:hover:not(:disabled) {
    background: var(--color-primary-hover, #E6187F);
    transform: scale(1.05);
  }

  .btn-submit-arrow:disabled {
    background: var(--color-border);
    color: var(--color-text-muted);
    cursor: not-allowed;
  }

  /* Recording mode styles */
  .btn-cancel-recording {
    width: 36px;
    min-height: 36px;
    max-height: 36px;
    padding: 0;
    border-radius: 50%;
    border: 2px solid rgba(239, 68, 68, 0.6);
    background: rgba(239, 68, 68, 0.12);
    color: var(--color-error);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .btn-cancel-recording:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: var(--color-error);
  }

  .recording-waveform {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 0 12px;
    min-width: 0;
  }

  .waveform-bars {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    flex: 1;
    justify-content: space-between;
    min-width: 0;
    overflow: hidden;
  }

  .waveform-bar {
    flex: 1;
    min-width: 4px;
    max-width: 14px;
    background: linear-gradient(to top, var(--color-primary), var(--color-primary-hover, #ff4da6));
    border-radius: 2px;
    transition: height 0.06s ease-out;
    min-height: 4px;
  }

  .recording-timer {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-primary);
    font-variant-numeric: tabular-nums;
  }

  .btn-submit-recording {
    width: 36px;
    min-height: 36px;
    max-height: 36px;
    padding: 0;
    border-radius: 50%;
    border: none;
    background: var(--color-primary);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .btn-submit-recording:hover {
    background: var(--color-primary-hover, #E6187F);
    transform: scale(1.05);
  }

  .processing-indicator {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: var(--color-text-muted);
    font-size: 0.9rem;
  }

  .loading-status {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-text-muted);
    font-size: 0.9rem;
  }

  .loading-status.below-input {
    justify-content: center;
    margin-top: 0.75rem;
  }

  .loading-eta {
    margin: 0.35rem 0 0;
    text-align: center;
    color: var(--color-text-light);
    font-size: 0.8rem;
  }

  .loading-text {
    white-space: nowrap;
  }

  /* Style VoiceRecorder inside chat input */
  .input-buttons :global(.voice-recorder) {
    display: flex;
    align-items: center;
  }

  .input-buttons :global(.mic-button),
  .input-buttons :global(.compact-mic) {
    width: 36px !important;
    height: 36px !important;
    min-height: 36px !important;
    max-height: 36px !important;
    border-radius: 50% !important;
    border: 1px solid var(--color-border) !important;
    background: transparent !important;
  }

  .input-buttons :global(.mic-button:hover),
  .input-buttons :global(.compact-mic:hover) {
    border-color: var(--color-primary) !important;
    color: var(--color-primary) !important;
    background: var(--color-primary-light) !important;
  }

  .input-buttons :global(.mic-button svg),
  .input-buttons :global(.compact-mic svg) {
    width: 18px !important;
    height: 18px !important;
  }

  /* Voice recorder mount - always rendered but mic only visible when idle */
  .voice-recorder-mount {
    position: absolute;
    right: 56px; /* Position before the submit button */
    top: 50%;
    transform: translateY(-50%);
    display: none;
    align-items: center;
    z-index: 2;
  }

  .voice-recorder-mount.show-mic {
    display: flex;
  }

  .voice-recorder-mount :global(.voice-recorder) {
    display: flex;
    align-items: center;
  }

  .voice-recorder-mount :global(.mic-button),
  .voice-recorder-mount :global(.compact-mic) {
    width: 36px !important;
    height: 36px !important;
    min-height: 36px !important;
    max-height: 36px !important;
    border-radius: 50% !important;
    border: 1px solid var(--color-border) !important;
    background: transparent !important;
  }

  .voice-recorder-mount :global(.mic-button:hover),
  .voice-recorder-mount :global(.compact-mic:hover) {
    border-color: var(--color-primary) !important;
    color: var(--color-primary) !important;
    background: var(--color-primary-light) !important;
  }

  .voice-recorder-mount :global(.mic-button svg),
  .voice-recorder-mount :global(.compact-mic svg) {
    width: 18px !important;
    height: 18px !important;
  }

  /* Cycling example prompt styling */
  textarea.showing-example {
    color: var(--color-text-muted);
    font-style: italic;
  }

  .wizard-qa {
    max-width: 800px;
    margin: 0 auto;
  }

  .question-progress {
    text-align: right;
    font-size: 0.875rem;
    color: var(--color-text-muted);
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .wizard-intro, .pair-intro { color: var(--color-text-muted); font-size: 0.9rem; text-align: center; }
  .wizard-intro { margin: -0.5rem 0 1.5rem; }
  .direct-start { display: flex; justify-content: center; align-items: center; flex-wrap: wrap; gap: 6px 14px; margin-top: 8px; }
  .direct-start button { min-height: 44px; font-size: 0.85rem; }
  .question-pair { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
  .pair-intro { margin: 0 0 1rem; }
  .variant-summary { color: var(--color-text-muted); font-size: 0.85rem; text-align: center; margin: 1rem 0; }
  .early-finish { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; margin-top: 1rem; }
  .early-finish span { font-size: 0.8rem; color: var(--color-text-muted); }
  .early-finish button { min-height: 44px; }
  .multi-select-hint { text-align: center; font-size: 0.8rem; }
  @media (max-width: 767px) {
    .question-pair { grid-template-columns: minmax(0, 1fr); gap: 0.75rem; }
    .wizard-container.with-questions { padding-bottom: calc(190px + env(safe-area-inset-bottom, 0px)); }
    .wizard-navigation {
      position: fixed; left: 10px; right: 10px; bottom: max(8px, env(safe-area-inset-bottom, 0px));
      z-index: 10; padding: 8px; border: 1px solid var(--color-border); border-radius: 12px;
      background: var(--color-surface); box-shadow: 0 3px 18px rgba(0, 0, 0, 0.1);
    }
    .wizard-navigation .wizard-actions { margin-top: 0; gap: 8px; }
    .wizard-navigation .wizard-actions button { min-height: 44px; flex: 1; font-size: 0.85rem; padding: 8px 10px; }
    .wizard-navigation .early-finish { margin-top: 4px; gap: 0; }
    .wizard-navigation .early-finish span { font-size: 0.7rem; }
  }
  @media (hover: none) and (pointer: coarse) { .multi-select-hint { display: none; } }

  .wizard-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 1rem;
  }

  .btn-outline {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    background: transparent;
    border: 2px solid var(--color-primary);
    color: var(--color-primary);
    border-radius: var(--radius);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-outline:hover {
    background: var(--color-primary);
    color: white;
    transform: translateY(-1px);
  }

  .error-message {
    background: var(--color-error);
    color: white;
    padding: 1rem;
    border-radius: var(--radius);
    text-align: center;
  }

  /* Generating combinations overlay */
  .generating-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .generating-content {
    background: var(--color-surface);
    padding: 2rem 3rem;
    border-radius: var(--radius);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .generating-title {
    margin: 0;
    color: var(--color-primary);
    font-weight: 600;
    font-size: 1.25rem;
  }

  .generating-subtitle {
    margin: 0.5rem 0 0 0;
    color: var(--color-text-muted);
    font-size: 0.9rem;
    max-width: 280px;
    text-align: center;
    line-height: 1.4;
  }

</style>
